import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Alert, Box, Button, CircularProgress, Grid, Link, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ResponsiveTable, { ResponsiveTableColumn } from '../components/common/ResponsiveTable';
import CustomerPicker from '../components/e2i/CustomerPicker';
import ProjectSearchPicker from '../components/e2i/ProjectSearchPicker';
import RegisterDialog from '../components/e2i/RegisterDialog';
import { RegisterChip } from '../components/e2i/StatusChip';
import useCompanyId from '../components/e2i/useCompanyId';
import e2iApi from '../services/e2iApi';
import { showSnackbar } from '../store/slices/uiSlice';
import type { CustomerOption, E2IConfig, PartyRef, RegisterCode, RegisterFilters, RegisterRow } from '../types/e2i';
import { REGISTER_CODES, REGISTER_NAMES } from '../types/e2i';
import { saveBlob } from '../utils/formattedSoa';
import { blobErrorText, cleanDateInput, dateOrNull, errorText, formatAmount, formatDate, quotationLabel, registerLabel, round2, todayIso } from '../utils/e2i';

/**
 * Enquiry to Invoice › Quotation Register: every enquiry with its estimate, quotation, register status, job, sales
 * order, contract dates and invoicing, as Horizon's register report. Exported to Excel by the server.
 */
const E2IRegister: React.FC = () => {
  const dispatch = useDispatch();
  const { companyId, companyName } = useCompanyId();
  const [config, setConfig] = useState<E2IConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState('');
  const [customer, setCustomer] = useState<CustomerOption | null>(null);
  const [project, setProject] = useState<PartyRef | null>(null);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [rows, setRows] = useState<RegisterRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [editing, setEditing] = useState<RegisterRow | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!companyId) { setError('Select a company first.'); setConfig(null); return undefined; }
    let cancelled = false;
    setError(null);
    setConfig(null);
    setCustomer(null);
    setProject(null);
    e2iApi.config(companyId)
      .then((c) => { if (!cancelled) setConfig(c); })
      .catch((e: any) => { if (!cancelled) setError(errorText(e, 'Enquiry to Invoice could not be opened for this company')); });
    return () => { cancelled = true; };
  }, [companyId]);

  const filters: RegisterFilters = useMemo(() => ({
    from: dateOrNull(from) || undefined,
    to: dateOrNull(to) || undefined,
    status: status || undefined,
    customer: customer?.code || undefined,
    project: project?.code || undefined,
    q: debounced || undefined,
  }), [from, to, status, customer, project, debounced]);

  useEffect(() => {
    if (!companyId || !config) return undefined;
    let cancelled = false;
    setLoading(true);
    setListError(null);
    e2iApi.register(companyId, filters)
      .then((r) => { if (!cancelled) setRows(r); })
      .catch((e: any) => { if (!cancelled) { setRows([]); setListError(errorText(e, 'The register could not be loaded')); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, config, filters, tick]);

  const currency = config?.currency || '';
  const registerCodes = config?.registerCodes?.length ? config.registerCodes : REGISTER_CODES.map((c) => ({ code: c, name: REGISTER_NAMES[c] }));
  const canSet = !!config?.capabilities.register;
  const totals = useMemo(() => ({
    quoted: round2(rows.reduce((s, r) => s + (Number(r.quotationTotal) || 0), 0)),
    invoiced: round2(rows.reduce((s, r) => s + (Number(r.invoicedTotal) || 0), 0)),
    pending: round2(rows.reduce((s, r) => s + (Number(r.pendingTotal) || 0), 0)),
  }), [rows]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const blob = await e2iApi.registerExport(companyId, filters);
      saveBlob(blob, `Quotation-Register-${todayIso()}.xlsx`);
    } catch (e: any) {
      dispatch(showSnackbar({ message: await blobErrorText(e, 'The Excel export failed'), severity: 'error' }));
    } finally {
      setExporting(false);
    }
  };

  const setRegister = async (code: RegisterCode, note: string) => {
    if (!editing) return;
    await e2iApi.setRegister(editing.enquiryId, code, note);
    setEditing(null);
    dispatch(showSnackbar({ message: `Register status of ${editing.enquiryNo || 'the enquiry'} set to ${registerLabel(code, null, config)}`, severity: 'success' }));
    setTick((t) => t + 1);
  };

  const openEdit = useCallback((r: RegisterRow) => setEditing(r), []);

  const columns: ResponsiveTableColumn<RegisterRow>[] = useMemo(() => [
    {
      id: 'enquiry',
      header: 'Enquiry',
      cell: (r) => (
        <Box sx={{ minWidth: 0 }}>
          <Link component={RouterLink} to={`/e2i/enquiries/${r.enquiryId}`} underline="hover" fontWeight={600}>{r.enquiryNo || 'Draft'}</Link>
          <Typography variant="caption" color="text.secondary" display="block">{formatDate(r.enquiryDate)}{r.estimateNo ? ` · ${r.estimateNo}` : ''}</Typography>
        </Box>
      ),
    },
    {
      id: 'customer',
      header: 'Customer and project',
      cell: (r) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2">{r.customer?.name || r.customer?.code}</Typography>
          {r.project ? <Typography variant="caption" color="text.secondary" display="block">{r.project.code}{r.project.name ? ` ${r.project.name}` : ''}</Typography> : null}
          <Typography variant="caption" color="text.secondary" display="block">{r.subject}</Typography>
        </Box>
      ),
    },
    {
      id: 'quotation',
      header: 'Quotation',
      cell: (r) => (r.quotationNo ? (
        <Box>
          <Typography variant="body2">{quotationLabel(r.quotationNo, r.quotationRevision)}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatDate(r.quotationDate)}{r.quotationTotal !== null && r.quotationTotal !== undefined ? ` · ${formatAmount(r.quotationTotal)} ${currency}` : ''}
          </Typography>
        </Box>
      ) : '—'),
    },
    {
      id: 'register',
      header: 'Register',
      cell: (r) => (
        <Stack spacing={0.5} alignItems="flex-start">
          <RegisterChip code={r.registerStatus} name={r.registerName} config={config} />
          {r.registerUpdatedAt ? <Typography variant="caption" color="text.secondary">since {formatDate(r.registerUpdatedAt)}</Typography> : null}
          {canSet ? <Button size="small" variant="text" onClick={() => openEdit(r)} aria-label={`Set the register status of ${r.enquiryNo || 'this enquiry'}`}>Set status</Button> : null}
        </Stack>
      ),
    },
    {
      id: 'job',
      header: 'Job and sales order',
      hideOnMobile: true,
      cell: (r) => (r.jobNo || r.salesOrderNum ? (
        <Box>
          <Typography variant="body2">{r.jobNo || '—'}{r.salesOrderNum ? ` · SO ${r.salesOrderNum}` : ''}</Typography>
          {r.contractStart || r.contractEnd ? (
            <Typography variant="caption" color="text.secondary" display="block">{formatDate(r.contractStart) || '…'} to {formatDate(r.contractEnd) || '…'}</Typography>
          ) : null}
          {r.clientPoRef ? <Typography variant="caption" color="text.secondary" display="block">PO {r.clientPoRef}</Typography> : null}
        </Box>
      ) : '—'),
    },
    {
      id: 'invoiced',
      header: `Invoiced ${currency}`.trim(),
      align: 'right',
      cell: (r) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{formatAmount(r.invoicedTotal)}</Typography>
          {r.pendingTotal ? <Typography variant="caption" color="text.secondary" display="block">{formatAmount(r.pendingTotal)} pending</Typography> : null}
          {r.invoiceNums?.length ? <Typography variant="caption" color="text.secondary" display="block">{r.invoiceNums.join(', ')}</Typography> : null}
        </Box>
      ),
    },
    { id: 'client', header: 'Last sent to client', hideOnMobile: true, cell: (r) => formatDate(r.lastClientSubmission) || '—' },
  ], [canSet, config, currency, openEdit]);

  const filtered = !!(filters.from || filters.to || filters.status || filters.customer || filters.project || filters.q);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 1500 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>Quotation register</Typography>
        {companyName ? <Typography variant="body2" color="text.secondary">{companyName}</Typography> : null}
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" startIcon={exporting ? <CircularProgress size={14} /> : <FileDownloadIcon />} onClick={() => { void exportExcel(); }} disabled={exporting || !config}>
          {exporting ? 'Exporting…' : 'Export to Excel'}
        </Button>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {config && !config.enabled ? <Alert severity="warning">Enquiry to Invoice is not switched on for {companyName || 'this company'} (Company Management).</Alert> : null}

      {config ? (
        <>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6} md={2}>
                <TextField id="e2i-reg-from" type="date" label="Enquiry date from" value={from} onChange={(e) => setFrom(cleanDateInput(e.target.value))} InputLabelProps={{ shrink: true }} fullWidth />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField id="e2i-reg-to" type="date" label="to" value={to} onChange={(e) => setTo(cleanDateInput(e.target.value))} InputLabelProps={{ shrink: true }} inputProps={{ min: filters.from }} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField id="e2i-reg-status" select label="Register status" value={status} onChange={(e) => setStatus(String(e.target.value))} fullWidth SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
                  <MenuItem value="">All</MenuItem>
                  {registerCodes.map((c) => <MenuItem key={c.code} value={String(c.code)}>{registerLabel(c.code, c.name)}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CustomerPicker id="e2i-reg-customer" companyId={companyId} value={customer} onChange={setCustomer} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <ProjectSearchPicker id="e2i-reg-project" companyId={companyId} label="Project" value={project} onChange={setProject} />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <TextField id="e2i-reg-search" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Enquiry, quotation, job, order or invoice number, subject" fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ height: '100%', alignItems: 'center', justifyContent: { md: 'flex-end' } }}>
                  <Typography variant="body2">{rows.length} enquir{rows.length === 1 ? 'y' : 'ies'}</Typography>
                  <Typography variant="body2">Quoted <strong>{formatAmount(totals.quoted)}</strong> {currency}</Typography>
                  <Typography variant="body2">Invoiced <strong>{formatAmount(totals.invoiced)}</strong> {currency}</Typography>
                  <Typography variant="body2">Pending <strong>{formatAmount(totals.pending)}</strong> {currency}</Typography>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
          {listError ? <Alert severity="error">{listError}</Alert> : null}
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2 }}><CircularProgress size={18} /><Typography variant="body2">Loading…</Typography></Stack>
          ) : (
            <ResponsiveTable ariaLabel="Quotation register" columns={columns} rows={rows} getRowKey={(r) => r.enquiryId} emptyMessage={filtered ? 'No enquiry matches the filters' : 'No enquiries yet'} />
          )}
        </>
      ) : !error ? (
        <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography variant="body2">Loading…</Typography></Stack>
      ) : null}

      <RegisterDialog
        open={!!editing}
        subject={editing?.enquiryNo || ''}
        current={editing?.registerStatus ?? null}
        config={config}
        onClose={() => setEditing(null)}
        onSave={setRegister}
      />
    </Box>
  );
};

export default E2IRegister;
